import { Inject, Injectable, InjectionToken } from '@angular/core';
import { collection, getDocs } from 'firebase/firestore';
import type { Firestore } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import type { Functions } from 'firebase/functions';
import { User } from 'src/app/interfaces';

export const CLEANUP_FIRESTORE = new InjectionToken<Firestore>('Cleanup Firestore');
export const CLEANUP_FUNCTIONS = new InjectionToken<Functions>('Cleanup Functions');

export interface CleanupDirectoryUser {
  uid: string;
  email: string | null;
  organisations: unknown;
}

export interface CleanupDirectory {
  organisationIds: string[];
  users: CleanupDirectoryUser[];
}

export interface CleanupOutcome {
  uid: string;
  email: string | null;
  action: 'updated' | 'deleted' | 'deleted-auth-missing' | 'failed';
  error: string | null;
}

export interface CleanupResult {
  affectedUserCount: number;
  updatedUserCount: number;
  deletedUserCount: number;
  warningCount: number;
  errorCount: number;
  auditLogWarning?: string;
  outcomes: CleanupOutcome[];
}

@Injectable({ providedIn: 'root' })
export class CleanOrphanedOrganisationUsersService {
  constructor(
    @Inject(CLEANUP_FIRESTORE) private firestore: Firestore,
    @Inject(CLEANUP_FUNCTIONS) private functions: Functions,
  ) {}

  async loadDirectory(): Promise<CleanupDirectory> {
    const [organisationSnapshot, usersSnapshot] = await Promise.all([
      getDocs(collection(this.firestore, 'organisations')),
      getDocs(collection(this.firestore, 'users')),
    ]);

    return {
      organisationIds: organisationSnapshot.docs.map((organisation) => organisation.id),
      users: usersSnapshot.docs.map((userDocument) => {
        const user = userDocument.data() as User;
        return {
          uid: userDocument.id,
          email: user.email ?? null,
          organisations: user.organisations,
        };
      }),
    };
  }

  async cleanUsers(organisationId: string): Promise<CleanupResult> {
    const callable = httpsCallable<{ organisationId: string }, CleanupResult>(
      this.functions,
      'httpCleanOrphanedOrganisationUsers',
    );
    const response = await callable({ organisationId });
    return response.data;
  }
}
